import * as React from "react";
import { observer, inject } from "mobx-react";
import { Link } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import { UncontrolledTooltip } from "reactstrap";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkSquareAlt, faSearch, faShoppingCart } from "@fortawesome/free-solid-svg-icons";

import { CustomerStore } from "../../stores/CustomerStore";
import { ConfigurationStore } from "../../stores/ConfigurationStore";
import { Contact, ContactInvolvement, BaseInvolvement, Customer } from "../../model/Customer";
import { SimpleHorseForSale, HorseForSaleState, HorseForSaleStateFriendlyName, HorseForSale } from "../../model/HorseForSale";

interface InvolvimentRow {
    id: string;
    type: string;
    horseForSale: HorseForSale;
}

const DEFAULT_SEARCH_BY_SALE = "Select a sale...";

export interface Props {
    customerStore?: CustomerStore;
    configurationStore?: ConfigurationStore;
    search: Boolean;
    involvements: ContactInvolvement;
    defaultTab: string;
    keyField: string;
    type?: string;
    currentInvolvement: boolean;
    defaultStateFilter?: string;
    needPagination?: boolean;
    selectAvailable?: boolean;
    selectedHorseForSale?: (horses: HorseForSale[]) => void;
    customer?: Customer;
}
export interface State {
    currentTab: string;
    searchSales: string;
    searchValue: string;

    // This is the list of selected rows (for swapping Hip # names)
    selectedHorsesForSale: Array<HorseForSale>;
}

@inject("configurationStore")
@inject("customerStore")
@observer
export default class InvolvementComponent extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            currentTab: this.props.defaultTab,
            searchSales: DEFAULT_SEARCH_BY_SALE,
            searchValue: "",
            selectedHorsesForSale: []
        };
    }

    filterStateSearch = (data: any) => {
        return this.state.searchSales === DEFAULT_SEARCH_BY_SALE
            ? data
            : data.filter((item: any) => item.horseForSale.state === this.state.searchSales);
    };

    defaultFilterState = (data: any, filter: string, remove: boolean) => {
        return remove
            ? data.filter((item: any) => item.horseForSale.state !== filter)
            : data.filter((item: any) => item.horseForSale.state === filter);
    };

    filterCurrentSale = (data: any) => {
        return data.filter((item: any) => item.horseForSale.sale.current);
    };

    filterbySale = (data: any) => {
        return data.filter((item: any) => item.horseForSale.sale.name === this.state.searchSales);
    };

    filterbyName = (data: any) => {
        return data.filter((item: any) => item.horseForSale.horse.name.toLowerCase().indexOf(this.state.searchValue.toLowerCase()) >= 0);
    };

    filterbyHipNumber = (data: any) => {
        return data.filter((item: any) => item.horseForSale.horse.id === this.state.searchValue);
    };

    // filterSearch = (data: any) => {
    //     if(filterbyName.)
    // }

    filterPastSale = (data: any) => {
        return data.filter((item: any) => !item.horseForSale.sale.current);
    };

    handleOnSelect = (row: InvolvimentRow, isSelect: boolean) => {
        if (isSelect) {
            this.setState(() => ({
                selectedHorsesForSale: [...this.state.selectedHorsesForSale, row.horseForSale]
            }));
        } else {
            this.setState(() => ({
                selectedHorsesForSale: this.state.selectedHorsesForSale.filter((x: HorseForSale) => x.id !== row.horseForSale.id)
            }));
        }
    };

    handleOnSelectAll = (isSelect: boolean, rows: InvolvimentRow[]) => {
        const hfs = rows.map(row => row.horseForSale);
        if (isSelect) {
            this.setState(() => ({
                selectedHorsesForSale: hfs
            }));
        } else {
            this.setState(() => ({ selectedHorsesForSale: [] }));
        }
    };

    render() {
        const configurationStore = this.props.configurationStore;
        const involvements = this.props.involvements;

        const deactivateCheckoutButton = this.state.selectedHorsesForSale.length === 0;

        const horseForCheckoutList = this.state.selectedHorsesForSale.map(h4s => h4s.id.toString().split("_")[1]);

        const selectRow = {
            mode: "checkbox",
            clickToSelect: true,
            selected:
                this.state.currentTab === "winningBidder"
                    ? (this.state.selectedHorsesForSale.map(hfs => "Winning Bidder_" + hfs.id) as Array<string>)
                    : [],
            onSelect: this.handleOnSelect,
            onSelectAll: this.handleOnSelectAll,
            hideSelectColumn: this.state.currentTab === "winningBidder" ? false : true
        };

        const mapFn = (involvement: any, type: string) => {
            const obj: { [field: string]: string | number | null } = {};
            obj.horseForSale = involvement.horseForSale;
            obj.type = type;
            obj.id = `${type}_${involvement.horseForSale.id}`;
            return obj;
        };

        let data: Array<any> = [];

        switch (this.state.currentTab) {
            case "consignor":
                data = involvements.consignorFor.map(involvement => mapFn(involvement, "Consignor"));
                break;
            case "buyer":
                data = involvements.buyerFor.map(involvement => mapFn(involvement, "Buyer"));
                break;
            case "owner":
                data = involvements.ownerFor.map(involvement => mapFn(involvement, "Owner"));
                break;
            case "winningBidder":
                data = involvements.winningBidderFor.map(involvement => mapFn(involvement, "Winning Bidder"));
                break;
            case "other":
                data = [
                    ...involvements.raisedByFor.map(involvement => mapFn(involvement, "Raised By")),
                    ...involvements.contactForHorses.map(involvement => mapFn(involvement, "Contact")),
                    ...involvements.signatureContactFor.map(involvement => mapFn(involvement, "Signature Name"))
                ];
                break;
            default:
                data = [
                    ...involvements.consignorFor.map(involvement => mapFn(involvement, "Consignor")),
                    ...involvements.buyerFor.map(involvement => mapFn(involvement, "Buyer")),
                    ...involvements.ownerFor.map(involvement => mapFn(involvement, "Owner")),
                    ...involvements.winningBidderFor.map(involvement => mapFn(involvement, "winning Bidder")),
                    ...involvements.raisedByFor.map(involvement => mapFn(involvement, "Raised By")),
                    ...involvements.contactForHorses.map(involvement => mapFn(involvement, "Contact")),
                    ...involvements.signatureContactFor.map(involvement => mapFn(involvement, "Signature Name"))
                ];
        }

        // Option to Default Filter
        data = this.props.defaultStateFilter ? this.defaultFilterState(data, this.props.defaultStateFilter, true) : data;

        // Filter Search State and Name
        data = this.state.searchSales !== "Select a sale..." ? this.filterbySale(data) : data;
        data = this.state.searchValue.length > 0 ? this.filterbyName(data) : data;
        // data = this.state.searchValue.length > 0 ? this.filterbyHipNumber(data) : data;

        // Current or Past Involvement
        data = (this.props.currentInvolvement ? this.filterCurrentSale(data) : this.filterPastSale(data)) || [];

        const columns = [
            {
                dataField: "horseForSale.sale.name",
                text: "Sale"
            },
            {
                dataField: "horseForSale.hipNumber",
                text: "Hip #"
            },
            {
                dataField: "horseName",
                text: "Name",
                formatter: (cellContent: any, row: BaseInvolvement) => {
                    return (
                        <div className="btn-group btn-group-toggle ml-3">
                            <Link to={`/horse/${row.horseForSale.horse.id}/`} title="Edit Horse">
                                {row.horseForSale.horse.name}
                            </Link>
                        </div>
                    );
                }
            },
            {
                dataField: "horseForSale.state",
                text: "State",
                formatter: (state: HorseForSaleState) => HorseForSaleStateFriendlyName(state)
            },
            {
                dataField: "type",
                text: "Role"
            },
            {
                dataField: "price",
                text: "Price",
                formatter: (cellContent: any, row: any) => {
                    return row.horseForSale.price
                        ? (row.horseForSale.price / 100).toLocaleString("en-CA", { style: "currency", currency: "CAD" })
                        : "-";
                }
            },
            {
                dataField: "id",
                text: "Actions",
                formatter: (cellContent: any, row: BaseInvolvement) => {
                    return (
                        <div className="btn-group btn-group-toggle ml-3">
                            <Link
                                to={`/horses-for-sale/${row.horseForSale.id}/application`}
                                className="btn btn-secondary btn-sm"
                                id={`tooltipOpenSale${row.horseForSale.id}`}
                                title="Open Sale"
                            >
                                <FontAwesomeIcon icon={faExternalLinkSquareAlt} />
                            </Link>
                            <UncontrolledTooltip placement="top" target={`tooltipOpenSale${row.horseForSale.id}`} delay={0} className="tooltipstrap">
                                Open Sale
                            </UncontrolledTooltip>
                        </div>
                    );
                }
            }
        ];

        return (
            <>
                {this.props.search && (
                    <div className="form-row">
                        <div className="col-6">
                            <div className="form-group">
                                <label htmlFor="firstname">Search</label>
                                <div className="input-group">
                                    <div className="input-group-prepend">
                                        <div className="input-group-text">
                                            <FontAwesomeIcon icon={faSearch} />
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="firstname"
                                        placeholder="Search for Hip # and horse name..."
                                        name="firstName"
                                        value={this.state.searchValue}
                                        onChange={e => this.setState({ searchValue: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group">
                                <label htmlFor="sale">Sale</label>
                                <select
                                    className="form-control not-required"
                                    name="sale"
                                    value={this.state.searchSales}
                                    onChange={e => this.setState({ searchSales: e.target.value })}
                                >
                                    <option value={DEFAULT_SEARCH_BY_SALE}>{DEFAULT_SEARCH_BY_SALE}</option>
                                    {configurationStore!.horseSales
                                        .sort((a: any, b: any) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                        .map((c: any, i: any) => (
                                            <option key={i} value={c.name}>
                                                {c.name}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                {this.state.currentTab === "winningBidder" && this.props.selectAvailable && (
                    <Link
                        className={`btn ${!deactivateCheckoutButton ? "btn-primary" : "btn-secondary disabled"} btn-sm mb-2`}
                        to={
                            !deactivateCheckoutButton
                                ? {
                                      pathname: `/checkout/${this.state.selectedHorsesForSale
                                          .map(hfs => hfs.id)
                                          .sort()
                                          .join("+")}/buyers`
                                  }
                                : {}
                        }
                    >
                        <FontAwesomeIcon icon={faShoppingCart} className="mr-2" />
                        Checkout the Selected Horses
                        <span className="badge badge-secondary ml-2">{this.state.selectedHorsesForSale.length}</span>
                    </Link>
                )}

                <ul className="nav nav-tabs">
                    <li className="nav-item">
                        <button
                            className={"nav-link" + " " + (this.state.currentTab === "all" ? "active" : "")}
                            onClick={() => this.setState({ currentTab: "all" })}
                        >
                            {" "}
                            All
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={"nav-link" + " " + (this.state.currentTab === "owner" ? "active" : "")}
                            onClick={() => this.setState({ currentTab: "owner" })}
                        >
                            {" "}
                            Owner
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={"nav-link" + " " + (this.state.currentTab === "consignor" ? "active" : "")}
                            onClick={() => this.setState({ currentTab: "consignor" })}
                        >
                            {" "}
                            Consignor
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={"nav-link" + " " + (this.state.currentTab === "winningBidder" ? "active" : "")}
                            onClick={() => this.setState({ currentTab: "winningBidder" })}
                        >
                            {" "}
                            Winning Bidder
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={"nav-link" + " " + (this.state.currentTab === "buyer" ? "active" : "")}
                            onClick={() => this.setState({ currentTab: "buyer" })}
                        >
                            {" "}
                            Buyer
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={"nav-link" + " " + (this.state.currentTab === "other" ? "active" : "")}
                            onClick={() => this.setState({ currentTab: "other" })}
                        >
                            {" "}
                            Other
                        </button>
                    </li>
                </ul>
                <div className="table-responsive">
                    <BootstrapTable
                        keyField="id"
                        remote={true}
                        hover={true}
                        bordered={false}
                        columns={columns}
                        bootstrap4={true}
                        data={data}
                        noDataIndication={() => "No Involvement."}
                        selectRow={selectRow}
                    />
                </div>
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/InvolvementComponent.tsx